import { useState, useEffect, useRef } from "react";
import { updateInventoryBatchExpiration } from "../../../server/InventoryUpdate";
import showAlert from "../../../server/ShowAlert";
import { expirationPatchErrorMessage } from "../../../utils/inventoryAvailability";
import { unallocatedSpotQuantity } from "../../../utils/inventoryTransfer";

// Server sends expiration as DD/MM/YYYY (legacy) or YYYY-MM-DD[THH:mm] (saved via app)
const toDisplayDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split(/[T ]/)[0]; // drop any time part
  if (datePart.includes("/")) return datePart; // already DD/MM/YYYY
  const [y, m, d] = datePart.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "-";
};

// <input type="date"> requires YYYY-MM-DD
const toInputDate = (value) => {
  if (!value) return "";
  const datePart = String(value).split(/[T ]/)[0];
  if (datePart.includes("/")) {
    const [d, m, y] = datePart.split("/");
    return d && m && y ? `${y}-${m}-${d}` : "";
  }
  return datePart; // already YYYY-MM-DD
};

const ProductLocationTable = ({
  locations,
  selectedRow,
  hasReturn,
  loadError = false,
  handleRowClick,
  isRowSelectable,
  onExpirationUpdated,
}) => {
  const [locationsRows, setLocationsRows] = useState(locations);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    setLocationsRows(locations);
  }, [locations]);

  const formatLocationLabel = (row) => {
    const zoneName =
      row?.zoneName || row?.zone?.name || row?.locationName || "";
    const spotId = row?.spotId || row?.id || "";
    const coords = String(spotId || "")
      .split("-")
      .slice(1)
      .map((part) => part.trim())
      .filter(Boolean)
      .join("-");
    if (zoneName && coords) return `${zoneName} | ${coords}`;
    return zoneName || coords || spotId || "-";
  };

  const startEditing = (batchId, currentDate, event) => {
    event.stopPropagation();
    skipBlurSaveRef.current = false;
    setEditingCell(batchId);
    setEditValue(toInputDate(currentDate));
  };

  const cancelEditing = (event) => {
    event?.stopPropagation();
    setEditingCell(null);
    setEditValue("");
  };

  const saveDate = (batchId, event) => {
    event?.stopPropagation();

    if (!editValue) {
      cancelEditing(event);
      return;
    }

    // Backend requires ISO-8601; editValue is already YYYY-MM-DD from <input type="date">
    updateInventoryBatchExpiration(batchId, editValue)
      .then(() => {
        setEditingCell(null);
        setEditValue("");
        setLocationsRows((prev) =>
          prev.map((loc) =>
            loc.batchId === batchId ? { ...loc, expiration: editValue } : loc,
          ),
        );
        if (typeof onExpirationUpdated === "function") onExpirationUpdated();
      })
      .catch((error) => {
        console.error("error update expired: ", error);
        showAlert({
          msgTitle: expirationPatchErrorMessage(error),
          msgType: "error",
        });
      });
  };

  const handleKeyDown = (event, batchId) => {
    event.stopPropagation();

    if (event.key === "Enter") {
      event.preventDefault();
      skipBlurSaveRef.current = true;
      saveDate(batchId, event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      skipBlurSaveRef.current = true;
      cancelEditing(event);
    }
  };

  const handleBlur = (batchId, event) => {
    if (skipBlurSaveRef.current) {
      skipBlurSaveRef.current = false;
      return;
    }
    saveDate(batchId, event);
  };

  const rowSelectable = (row) =>
    typeof isRowSelectable === "function" ? isRowSelectable(row) : true;

  return (
    <div className="product-location-table">
      <table>
        <tbody>
          <tr>
            <th>מיקום</th>
            <th>כמות</th>
            <th>כמות ניתנת להעברה</th>
            <th>תוקף</th>
          </tr>
          {locationsRows.length ? (
            locationsRows.map((x) => {
              const selectable = rowSelectable(x);
              return (
                <tr
                  className={[
                    selectedRow?.id === x.id
                      ? "location-row active"
                      : "location-row",
                    selectable ? "" : "disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  key={x.id}
                  onClick={() => {
                    if (!selectable) return;
                    handleRowClick(x);
                  }}
                >
                  <td data-label="מיקום" className="location-col">
                    <div
                      className="location-cell"
                      title={formatLocationLabel(x)}
                    >
                      {formatLocationLabel(x)}
                    </div>
                  </td>
                  <td data-label="כמות">{x.quantity}</td>
                  <td data-label="כמות ניתנת להעברה">
                    {unallocatedSpotQuantity(x)}
                  </td>
                  <td className="date-cell" data-label="תוקף">
                    {editingCell === x.batchId ? (
                      <div
                        className="date-edit-container"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="date"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, x.batchId)}
                          onBlur={(e) => handleBlur(x.batchId, e)}
                          className="date-input"
                          autoFocus
                          title="Enter לשמירה, Escape לביטול"
                          aria-label="תוקף"
                        />
                      </div>
                    ) : (
                      <span
                        className="date-display"
                        onClick={(e) =>
                          startEditing(x.batchId, x.expiration, e)
                        }
                        title="לחץ לעריכה"
                      >
                        {toDisplayDate(x.expiration)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          ) : loadError ? (
            <tr>
              <td colSpan="4">שגיאה בטעינת מיקומי מלאי</td>
            </tr>
          ) : hasReturn ? (
            <tr>
              <td colSpan="4">לא נמצאו מיקומים במלאי</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default ProductLocationTable;
